import React, { useEffect, useLayoutEffect } from "react";
import Layout from "../components/layout";
import SEO from "../components/seo";
import { graphql } from "gatsby";
import useStoryblok from "../lib/storyblok";
import { sbEditable } from "@storyblok/storyblok-editable";
import DynamicComponent from "../components/dynamicComponent";
import { Helmet } from "react-helmet";
import RelatedProjects from "../sections/Work/RelatedProjects";
import RelatedBlogs from "../sections/Blog/RelatedBlogs";
import ScopeHeader from "../sections/Work/ScopeHeader";
import TagHeader from "../sections/Blog/TagHeader";
import ServicesHeader from "../sections/Services/ServicesHeader";
import Popup from "../components/Popup";

const DoNotIndex = () => {
  return <Helmet robots={`noindex`} />;
};

function StoryblokEntry({ data, location }) {

  if (
    data.storyblokEntry.full_slug == `footer` ||
    data.storyblokEntry.full_slug == `header`
  ) {
    return DoNotIndex;
  }

  let story = data.storyblokEntry;

  story = useStoryblok(story, location);

  const components = story.content.blocks?.map((blok) => {
    return <DynamicComponent blok={blok} key={blok._uid} />;
  });

  const hasRelatedProjects = story.content.related_project?.length > 0 || false;

  const hasRelatedBlogs = story.content.related_blogs?.length > 0 || false;

  const blogContent = story?.field_component === "blog_page" ? story : null;

  const oneOrMoreTags = story.tag_list?.length > 0;

  // if the page is a service page

  const isServicePage = data.storyblokEntry.full_slug?.includes("services/") && data.storyblokEntry.full_slug != "services/";

  const ssOverride = story?.content?.card_image?.filename || null;

  useLayoutEffect(() => {
    // only run 1 time per session
    if (typeof window !== "undefined") {
      const hasRanTracking = window.sessionStorage.getItem("hasRanTracking");
      if (hasRanTracking == undefined) {
        window.sessionStorage.setItem("hasRanTracking", true);

        // get referrer, and utm_source, utm_medium, utm_campaign, utm_term, utm_content

        const referrer = document.referrer;
        const utm_source = new URLSearchParams(window.location.search).get("utm_source") || "";
        const utm_medium = new URLSearchParams(window.location.search).get("utm_medium") || "";
        const utm_campaign = new URLSearchParams(window.location.search).get("utm_campaign") || "";
        const utm_term = new URLSearchParams(window.location.search).get("utm_term") || "";
        const utm_content = new URLSearchParams(window.location.search).get("utm_content") || "";

        // get date in DD/MM/YYYY format

        const date = new Date().toLocaleDateString("en-GB");

        const landingPage = window.location.href;

        const data = {
          referrer,
          utm_source,
          utm_medium,
          utm_campaign,
          utm_term,
          utm_content,
          date,
          landingPage
        }

        // check if localStorage is available

        if (typeof window.localStorage !== "undefined") {
          // check if we have a key called "tracking_first_visit" 
          // if we do, then we know that this is not the first visit
          // if we don't, then we know that this is the first visit

          const hasVisitedBefore = window.localStorage.getItem("tracking_first_visit");

          if (hasVisitedBefore == undefined) {
            // set data in localStorage
            window.localStorage.setItem("tracking_first_visit", JSON.stringify(data));
          }

          // set current visit data in localStorage

          window.localStorage.setItem("tracking_current_visit", JSON.stringify(data));

        }
      }


    }
  }, []);

  return (
    <Layout
      location={location.pathname}
      blackNav={story.content.black_nav || false}
      navStyle={story.content.nav_style || `white`}
      lineUnderNav={story.content.line_under_nav || false}
      hideLineMobile={story.content.hide_line_under_nav_mobile || false}
      tagList={data.storyblokEntry.tag_list}
      blogContent={blogContent}
      animateLogo={story.content.animate_logo || false}
      {...sbEditable(story.content)}
    >
      <SEO
        keywords={[`Willow & Blake`]}
        title={story.content.seo_title || ""}
        description={story.content.seo_description || ""}
        socialSharing={story.content.social_sharing?.filename || ""}
        ssOverride={ssOverride}
      />

      {
        story?.field_component === "work_page" && oneOrMoreTags ? (
          <ScopeHeader tagList={story.tag_list} navStyle={story.content.nav_style} />
        ) : null
      }


      {
        story?.field_component === "blog_page" ? (
          <TagHeader tagList={story.tag_list} />
        ) : null
      }

      {
        isServicePage && (
          <ServicesHeader currentPage={data.storyblokEntry.full_slug} />
        )
      }

      <h1 className="sr-only">{story.content.seo_title == "Branding Agency | Willow and Blake" ? `` : story.content.seo_title}</h1>

      {components}


      {
        hasRelatedProjects && story.content.related_project != [] && (
          <RelatedProjects projects={story.content.related_project} />
        )
      }

      {
        hasRelatedBlogs && story.content.related_blogs != [] && (
          <RelatedBlogs projects={story.content.related_blogs} />
        )
      }

      <Popup />


    </Layout>
  );
}

export const query = graphql`
  query ($full_slug: String!, $formatString: String = "MMMM Do, YYYY") {
    storyblokEntry(full_slug: { eq: $full_slug }) {
      id
      name
      full_slug
      content
      field_component
      tag_list
      created_at(formatString: $formatString)
      first_published_at(formatString: "DD.MM.YY")
    }
  }
`;

export default StoryblokEntry;
